import { useEffect, useState } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoIcon } from '@/components/ui/Logo';
import {
  GoogleSignInButton,
  useGoogleAuthErrorMessage,
} from '@/components/auth/GoogleSignInButton';
import { Loader2, AlertCircle, Mail, Lock, ArrowRight } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const login = useAuthStore((state) => state.login);
  const googleErrorMessage = useGoogleAuthErrorMessage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // A failed Google round-trip can bounce back here with ?google_error=<code>
  // (e.g. the user cancelled at the consent screen). Surface it in the same
  // error banner the password form uses, then scrub it from the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('google_error');
    if (!code) return;
    setError(googleErrorMessage(code));
    params.delete('google_error');
    const qs = params.toString();
    window.history.replaceState(null, '', `/login${qs ? `?${qs}` : ''}`);
    // Read once on mount — the URL is scrubbed immediately after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      navigate({ to: '/dashboard' });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth:login.errors.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-[var(--color-base)] text-[var(--color-text)]">
      {/* ── Left: form pane ── */}
      <div className="relative w-full lg:w-[480px] flex flex-col p-8 lg:p-12 z-10 bg-[var(--color-base)] shadow-[20px_0_40px_rgba(0,0,0,0.3)]">
        {/* Brand pinned top */}
        <button
          onClick={() => navigate({ to: '/' })}
          className="flex items-center gap-1.5 self-start"
          aria-label={t('a11y:backHome')}
        >
          <LogoIcon size="lg" />
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <span className="font-logo text-2xl leading-none">my360</span>
        </button>

        {/* Form vertically centered */}
        <div className="flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
          >
            <h1 className="text-3xl font-semibold mb-2 tracking-tight">
              {t('auth:login.welcomeBack')}
            </h1>
            <p className="text-[var(--color-text-muted)] mb-8 max-w-sm">
              {t('auth:login.enterDetails')}
            </p>

            {/* Provider CTA sits above the form (renders nothing when the
                backend has no Google credentials configured). */}
            <GoogleSignInButton redirectTo="/dashboard" disabled={isLoading} />

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 p-3 text-sm text-[var(--color-destructive)] animate-shake">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-1">
                <label className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-widest pl-1">
                  {t('auth:login.email')}
                </label>
                <Input
                  type="email"
                  placeholder={t('auth:login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  icon={<Mail className="w-4 h-4" />}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between pl-1">
                  <label className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-widest">
                    {t('auth:login.password')}
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-[var(--color-primary)] hover:underline"
                  >
                    {t('auth:login.forgotPasswordLink')}
                  </Link>
                </div>
                <Input
                  type="password"
                  placeholder={t('auth:login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  icon={<Lock className="w-4 h-4" />}
                  required
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" variant="primary" disabled={isLoading} className="w-full h-11 mt-6">
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth:login.signingIn')}
                  </>
                ) : (
                  <>
                    {t('auth:login.signIn')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-sm text-[var(--color-text-muted)] text-center mt-8">
              {t('auth:login.noAccountQuestion')}{' '}
              <button
                onClick={() => navigate({ to: '/register' })}
                className="text-[var(--color-text)] hover:text-[var(--color-primary)] transition-colors font-medium"
              >
                {t('auth:login.signUpLink')}
              </button>
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── Right: ambient pane ── */}
      <div className="hidden lg:block relative flex-1 bg-[var(--color-surface)] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2670&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-30 animate-[pulse_10s_ease-in-out_infinite]"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[var(--color-base)] opacity-50" />

        {/* Orbital rings */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 100, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full pointer-events-none"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 150, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[var(--color-primary)]/10 rounded-full pointer-events-none"
        />

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 glass-panel p-6 rounded-2xl max-w-sm ml-20">
          <p className="font-serif italic text-xl text-white/80 mb-4 leading-relaxed">
            "{t('auth:login.testimonialQuote')}"
          </p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-accent)]" />
            <div>
              <div className="text-sm font-medium text-white">{t('auth:login.testimonialName')}</div>
              <div className="text-xs text-white/50">{t('auth:login.testimonialRole')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
